import { productAlt } from '../../server/store-collections.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, BadgeCheck, Instagram, MapPin, MessageCircle, Plus, Ruler, Search, Tag } from 'lucide-react';
import LazyModelViewer from '../LazyModelViewer.jsx';
import { categoryName, gridCols, isVideo, photoAt, priceTag, storeImageUrl, track } from './api.js';
import { Picture, Reveal, useScrollProgress } from './ui.jsx';

const promiseIcons = [MapPin, Ruler, Tag, MessageCircle, BadgeCheck];

// A product card. Pointing at it starts loading the product, so opening it feels instant.
export function ProductTile({ product, onOpen, onPrefetch, i = 0 }) {
  // A draft (shown to staff only) has nothing public to prefetch.
  const warm = () => { if (product.live !== false) onPrefetch?.(product); };
  const beta = product.purchase_availability === 'beta';
  return <Reveal as="button" type="button" i={i} className="shop-tile" onClick={() => onOpen(product)} onPointerEnter={warm} onFocus={warm} onTouchStart={warm}>
    <span className="shop-tile-media"><Picture src={product.image} alt={productAlt(product)} sizes="(max-width: 700px) 100vw, (max-width: 1100px) 50vw, 400px" /></span>
    <span className="shop-tile-body">
      <small>{categoryName(product.category)}{product.collection ? ` · ${product.collection}` : ''}{product.live === false ? ' · Draft, only you can see this' : ''}</small>
      <strong>{product.name}</strong>
      {product.subtitle && <span>{product.subtitle}</span>}
      <span className="shop-tile-foot">
        {beta ? <b className="shop-badge">Beta · coming soon</b> : <b>{priceTag(product.price_cents, product.currency)}</b>}
        <em>{beta ? 'Take a look' : 'View'}<ArrowRight /></em>
      </span>
    </span>
  </Reveal>;
}

// The moving strip under the hero. It always fills the screen: with only a few products they repeat, and each new
// product released takes the place of a repeat.
function ProductStrip({ products, onOpen, onPrefetch }) {
  const [copies, setCopies] = useState(2);
  useEffect(() => {
    const fit = () => setCopies(Math.max(1, Math.ceil((window.innerWidth + 260) / (Math.max(products.length, 1) * 232))));
    fit(); window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [products.length]);
  if (!products.length) return null;
  // One "set" is wide enough to cover the screen; two sets side by side loop without a seam.
  const set = Array.from({ length: copies }, () => products).flat();
  const card = (product, index, hidden) => <button type="button" key={`${hidden ? 'b' : 'a'}-${index}`} className="shop-strip-card" tabIndex={hidden ? -1 : 0} aria-hidden={hidden || undefined}
    onClick={() => onOpen(product)} onPointerEnter={() => onPrefetch?.(product)} onTouchStart={() => onPrefetch?.(product)}>
    <span className="shop-strip-media"><Picture src={product.image} alt="" sizes="220px" /></span>
    <span className="shop-strip-name">{product.name}</span>
  </button>;
  return <section className="shop-strip" aria-label="Products">
    <div className="shop-strip-track" style={{ '--items': set.length }}>
      {set.map((product, index) => card(product, index, false))}
      {set.map((product, index) => card(product, index, true))}
    </div>
  </section>;
}

function Hero({ site, onShop, mobile }) {
  const hero = useRef(null);
  useScrollProgress(hero, 'exit');
  const image = (mobile ? site.hero.image_mobile : site.hero.image) || site.hero.image || site.hero.image_mobile;
  return <section className="shop-hero" ref={hero}>
    <div className="shop-hero-media"><Picture key={image} src={image} alt="" sizes="100vw" priority /></div>
    <div className="shop-wrap">
      <div className="shop-hero-copy">
        {site.hero.eyebrow && <span className="shop-eyebrow" style={{ '--i': 0 }}>{site.hero.eyebrow}</span>}
        <h1 style={{ '--i': 1 }}>{site.hero.headline}</h1>
        {site.hero.subheadline && <p style={{ '--i': 2 }}>{site.hero.subheadline}</p>}
        <div className="shop-hero-actions" style={{ '--i': 3 }}>
          <button type="button" className="shop-btn light" onClick={onShop}>{site.hero.button}<ArrowRight /></button>
        </div>
      </div>
    </div>
  </section>;
}

// The featured product, centre stage: its name, then the 3D model itself turning slowly in a pool of light.
// The model is only fetched once the section is near the screen; until then (and if there is no model) its photo shows.
function Spotlight({ product, onOpen, onPrefetch }) {
  const section = useRef(null);
  const [near, setNear] = useState(false);
  useScrollProgress(section, 'through');
  useEffect(() => {
    const element = section.current;
    if (!element || typeof IntersectionObserver === 'undefined') { setNear(true); return undefined; }
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setNear(true); observer.disconnect(); } }, { rootMargin: '500px 0px' });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  if (!product) return null;
  const beta = product.purchase_availability === 'beta';
  const config = { ...(product.viewer_config || {}), auto_rotate: true };
  return <section className="shop-spotlight" ref={section} onPointerEnter={() => onPrefetch?.(product)}>
    <div className="shop-wrap shop-spotlight-copy">
      <Reveal as="span" className="shop-eyebrow">{beta ? 'Coming soon' : 'Featured'}</Reveal>
      <Reveal as="h2" i={1}>{product.name}</Reveal>
      {product.subtitle && <Reveal as="p" i={2}>{product.subtitle}</Reveal>}
      <Reveal i={3} className="shop-spotlight-actions">
        <button type="button" className="shop-btn" onClick={() => onOpen(product)}>{product.launch_page ? 'See it in action' : 'Take a closer look'}<ArrowRight /></button>
        {beta ? <span className="shop-badge">Beta</span> : <span className="shop-spotlight-price">{priceTag(product.price_cents, product.currency)}</span>}
      </Reveal>
    </div>
    <button type="button" className="shop-spotlight-stage" onClick={() => onOpen(product)} aria-label={`Open ${product.name}`}>
      <span className="shop-spotlight-model">
        {product.model_url && near
          ? <LazyModelViewer key={product.slug} modelUrl={product.model_url} config={config} showControls={false} showViewAngle={false} fallbackImageUrl={photoAt(product.image, 1080)} fallbackAlt="" ariaLabel="" />
          : <Picture src={product.image} alt="" sizes="(max-width: 900px) 100vw, 900px" />}
      </span>
    </button>
  </section>;
}

// Every real picture a product has: its main photo, its gallery, and the pictures in its detail sections.
function productPhotos(product) {
  const blocks = Array.isArray(product.viewer_config?.product_blocks) ? product.viewer_config.product_blocks : [];
  const gallery = (product.gallery || []).map((item) => typeof item === 'string' ? item : item?.url || item?.image);
  const inBlocks = blocks.filter((block) => block && !block.hidden).flatMap((block) => [block.image, block.media_url]);
  return [...new Set([product.image, ...gallery, ...inBlocks].filter((path) => typeof path === 'string' && storeImageUrl(path, 480) && !isVideo(path)))];
}

// A wall of the store's own photos, laid out unevenly on purpose. Each one opens its product.
function PhotoWall({ products, onOpen, onPrefetch }) {
  const shots = useMemo(() => {
    const seen = new Set(), out = [];
    // One photo per product first, then second photos, so no single product fills the wall.
    for (let round = 0; round < 4 && out.length < 5; round++) for (const product of products) {
      const path = productPhotos(product)[round];
      if (path && !seen.has(path) && out.length < 5) { seen.add(path); out.push({ path, product }); }
    }
    return out;
  }, [products]);
  if (shots.length < 3) return null;
  return <section className="shop-section"><div className="shop-wrap">
    <Reveal className="shop-section-head"><span className="shop-eyebrow">Up close</span><h2 className="shop-title">Made to be looked at twice.</h2></Reveal>
    <div className={`shop-wall shop-wall-${shots.length}`}>
      {shots.map((shot, index) => <Reveal as="button" type="button" key={shot.path} i={index % 3} className="shop-wall-shot" onClick={() => onOpen(shot.product)} onPointerEnter={() => onPrefetch?.(shot.product)}>
        <Picture src={shot.path} alt={shot.product.name} sizes="(max-width: 900px) 100vw, 720px" />
        <span className="shop-wall-name">{shot.product.name}<ArrowRight /></span>
      </Reveal>)}
    </div>
  </div></section>;
}

// The promises, written large across one of the store's own photos.
function PromiseBand({ promises, image }) {
  const band = useRef(null);
  useScrollProgress(band, 'through');
  if (!promises.length) return null;
  return <section className={`shop-band ${image ? 'has-photo' : ''}`} ref={band}>
    {image && <div className="shop-band-media"><Picture src={image} alt="" sizes="100vw" /></div>}
    <div className="shop-wrap shop-band-list">
      {promises.map((text, index) => { const Icon = promiseIcons[index % promiseIcons.length]; return <Reveal key={text} i={index} className="shop-band-item"><Icon /><strong>{text}</strong></Reveal>; })}
    </div>
  </section>;
}

function StoryRow({ item, product, onOpen }) {
  const media = useRef(null);
  useScrollProgress(media, 'through');
  return <div className="shop-story-row">
    <Reveal className="shop-story-media" ><div ref={media} style={{ width: '100%', height: '100%' }}><Picture src={item.image || product?.image} alt="" sizes="(max-width: 900px) 100vw, 600px" /></div></Reveal>
    <Reveal className="shop-story-copy" i={1}>
      <h3>{item.title}</h3>
      {item.text && <p>{item.text}</p>}
      {product && <button type="button" className="shop-link" onClick={() => onOpen(product)}>See the {product.name}<ArrowRight /></button>}
    </Reveal>
  </div>;
}

// `tabs` are the collections shown as filters (set in the store's settings). A collection that is not a tab is
// simply not offered as a filter; its products still appear under All.
export function Catalog({ products, category, onCategory, search, onSearch, tabs = [], onOpen, onPrefetch, heading = true }) {
  const categories = useMemo(() => ['All', ...tabs.map((tab) => tab.category)], [tabs]);
  const shown = products.filter((product) => (category === 'All' || categoryName(product.category) === category)
    && (!search.trim() || `${product.name} ${product.subtitle} ${product.collection} ${product.category}`.toLowerCase().includes(search.trim().toLowerCase())));
  return <section className="shop-section" id="products">
    <div className="shop-wrap">
      {heading && <Reveal className="shop-section-head"><span className="shop-eyebrow">The lineup</span><h2 className="shop-title">Parts that fit like they came with the car.</h2></Reveal>}
      {(categories.length > 2 || products.length > 6) && <div className="shop-filter">
        {categories.length > 2 && categories.map((name) => <button type="button" key={name} aria-pressed={category === name} onClick={() => onCategory(name)}>{tabs.find((tab) => tab.category === name)?.label || name}</button>)}
        {products.length > 6 && <label className="shop-search"><Search /><span className="shop-sr">Search products</span><input value={search} onChange={(event) => onSearch(event.target.value)} placeholder="Search" /></label>}
      </div>}
      {shown.length ? <div className="shop-grid" style={{ '--cols': gridCols(shown.length) }}>{shown.map((product, index) => <ProductTile key={product.id} product={product} onOpen={onOpen} onPrefetch={onPrefetch} i={index % 3} />)}</div>
        : <p className="shop-empty">Nothing matches that yet.</p>}
    </div>
  </section>;
}

export default function Home({ data, drafts = [], mobile, catalog, onOpen, onPrefetch, onCatalog, onNaMiata }) {
  const { site, products = [], flagship } = data;
  const byId = (id) => products.find((product) => product.id === id);
  return <>
    <Hero site={site} mobile={mobile} onShop={() => onCatalog('All')} />
    {/* Who we are and what we make, in one line (search engines read it; it links to the NA Miata page). */}
    <section className="shop-intro"><div className="shop-wrap"><p>3DLabs (3DL) designs and 3D prints <a href="/store/na-miata-accessories" onClick={(event) => { if (onNaMiata) { event.preventDefault(); onNaMiata(); } }}>NA Miata accessories</a> and interior parts, made to order in Ontario.</p></div></section>
    <ProductStrip products={products} onOpen={onOpen} onPrefetch={onPrefetch} />
    <Spotlight product={flagship} onOpen={onOpen} onPrefetch={onPrefetch} />
    {site.highlights.length > 0 && <section className="shop-section"><div className="shop-wrap shop-story">
      {site.highlights.map((item) => <StoryRow key={item.id} item={item} product={byId(item.product_id)} onOpen={onOpen} />)}
    </div></section>}
    <Catalog products={[...products, ...drafts]} {...catalog} onOpen={onOpen} onPrefetch={onPrefetch} />
    <PromiseBand promises={site.promises} image={site.hero.image_mobile && site.hero.image_mobile !== site.hero.image ? site.hero.image_mobile : ''} />
    <PhotoWall products={products} onOpen={onOpen} onPrefetch={onPrefetch} />
    {site.faq.length > 0 && <section className="shop-section"><div className="shop-wrap">
      <Reveal className="shop-section-head"><span className="shop-eyebrow">Questions</span><h2 className="shop-title">Good to know.</h2></Reveal>
      <Reveal className="shop-faq">{site.faq.map((item) => <details key={item.q}><summary>{item.q}<Plus /></summary><p>{item.a}</p></details>)}</Reveal>
    </div></section>}
    <section className="shop-section"><div className="shop-wrap">
      <Reveal className="shop-contact">
        <h2 className="shop-title">{site.contact.title}</h2>
        {site.contact.text && <p>{site.contact.text}</p>}
        {/* Instagram messages are the main way to reach 3DLabs; email stays as the second option. */}
        {site.contact.instagram && <a className="shop-btn light" href={site.contact.instagram} onClick={() => track('store_contact_click', null, { channel: 'instagram', source: 'home' })}><Instagram />Message us on Instagram</a>}
        {site.contact.email && <a className={site.contact.instagram ? 'shop-link' : 'shop-btn light'} href={`mailto:${site.contact.email}?subject=${encodeURIComponent('Custom part enquiry')}`} onClick={() => track('store_contact_click', null, { channel: 'email', source: 'home' })}>{site.contact.instagram ? 'Or email us' : site.contact.button}<ArrowRight /></a>}
      </Reveal>
    </div></section>
  </>;
}
